import { NgClass } from '@angular/common';

import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';

import { map, Observable, Subscription } from 'rxjs';

import { FileContent, FileContentFactory } from '../../models';

import { ModifyAdultFamilyMember, ModifyAdultFamilyMemberFactory } from '../../models/kng/adultFamilyMember.model';

import { FileInputComponent } from '../file-input/file-input.component';



@Component({

    selector: 'valben-adult-family-member',

    standalone: true,

    imports: [NgClass, FormsModule, FileInputComponent],

    templateUrl: './adult-family-member.component.html',

})

export class AdultFamilyMemberComponent implements OnInit, OnDestroy {

    @Input() index: number = 0;

    @Input() itemObservable!: () => Observable<any> | undefined;

    @Input() update!: (payload: { index: number; member: ModifyAdultFamilyMember }) => void;

    @Input() preview: boolean = false;

    @Input() error: boolean = false;

    @Input() showMonthlyIncome: boolean = false;



    fullName: string = '';

    monthlyIncomeValue: string = '';



    private itemSubscription?: Subscription;

    private _latestValue: any;



    readonly taxDocumentItemObservable = () => this.itemObservable()?.pipe(

        map((value) => ({

            taxIncomeDocument: value?.adultFamilyMembers?.[this.index]?.taxIncomeCertificate?.document ?? FileContentFactory.create(),

        }))

    );



    readonly ownsPropertyDocumentItemObservable = () => this.itemObservable()?.pipe(

        map((value) => ({

            ownsPropertyDocument: value?.adultFamilyMembers?.[this.index]?.ownsPropertyDocument ?? FileContentFactory.create(),

        }))

    );



    ngOnInit(): void {

        this.itemSubscription = this.itemObservable()?.subscribe((value) => {

            if (!value) return;

            this._latestValue = value;

            const member = value.adultFamilyMembers?.[this.index];

            const nextFullName = member?.fullName ?? '';

            const monthlyIncome = member?.taxIncomeCertificate?.monthlyIncome;

            const nextMonthlyIncomeValue = monthlyIncome === null || monthlyIncome === undefined || Number.isNaN(monthlyIncome)

                ? ''

                : monthlyIncome.toString();



            if (nextFullName !== this.fullName) {

                this.fullName = nextFullName;

            }

            if (nextMonthlyIncomeValue !== this.monthlyIncomeValue) {

                this.monthlyIncomeValue = nextMonthlyIncomeValue;

            }

        });

    }



    ngOnDestroy(): void {

        this.itemSubscription?.unsubscribe();

    }



    onFullNameChange(): void {

        this.patchMember({ fullName: this.fullName });

    }



    onTaxDocumentChange = (document: FileContent): void => {

        this.patchMember({

            taxIncomeCertificate: {

                document,

                monthlyIncome: this.parseMonthlyIncome(),

            },

        });

    };



    onOwnsPropertyDocumentChange = (document: FileContent): void => {

        this.patchMember({ ownsPropertyDocument: document });

    };



    onMonthlyIncomeChange(): void {

        const parsed = this.parseMonthlyIncome();

        const current = this.getCurrentMember();

        if (current.taxIncomeCertificate.monthlyIncome === parsed) {

            return;

        }

        this.patchMember({

            taxIncomeCertificate: {

                ...current.taxIncomeCertificate,

                monthlyIncome: parsed,

            },

        });

    }



    private parseMonthlyIncome(): number {

        if (this.monthlyIncomeValue === '') return 0;

        const parsed = Number(this.monthlyIncomeValue);

        return !isNaN(parsed) ? parsed : 0;

    }



    private getCurrentMember(): ModifyAdultFamilyMember {

        return this._latestValue?.adultFamilyMembers?.[this.index] ?? ModifyAdultFamilyMemberFactory.create();

    }



    private patchMember(partial: Partial<ModifyAdultFamilyMember> & {

        taxIncomeCertificate?: ModifyAdultFamilyMember['taxIncomeCertificate'];

    }): void {

        const current = this.getCurrentMember();

        const member: ModifyAdultFamilyMember = {

            ...current,

            ...partial,

            taxIncomeCertificate: partial.taxIncomeCertificate ?? current.taxIncomeCertificate,

            ownsPropertyDocument: partial.ownsPropertyDocument ?? current.ownsPropertyDocument,

        };

        this.update({ index: this.index, member });

    }

}

